import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Text, Title } from '@repletoryreach/sdk-ui';
import './hub-card.css';

export interface HubRow {
  /** What the destination is called, in the operator's words. */
  label: ReactNode;
  to: string;
  /** A live count or state, right-aligned before the chevron. Omit while a query is in flight. */
  meta?: ReactNode;
}

export interface HubCardProps {
  /** An 22px line icon. The card supplies the colour, so draw with `currentColor`. */
  icon: ReactNode;
  title: ReactNode;
  /** One sentence saying what this group of pages covers. */
  description: ReactNode;
  rows: HubRow[];
  /** A badge beside the title, for a group that can report its own condition. */
  status?: ReactNode;
}

const Chevron = (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M6 3.5L10.5 8L6 12.5" />
  </svg>
);

/**
 * One group of destinations on a hub page: an icon, what the group covers, and
 * a labelled row per page underneath.
 *
 * A hub built from tiles gives every child a card of its own, which turns seven
 * children into seven boxes an operator has to read before finding the one they
 * came for. Grouping them puts the whole map on one screen, and the row rather
 * than the card is the link, so the target is exactly the thing that is named.
 */
export function HubCard({ icon, title, description, rows, status }: HubCardProps) {
  return (
    <section className="rr-hub-card">
      <div className="rr-hub-card__head">
        <span className="rr-hub-card__icon" aria-hidden>{icon}</span>
        <div className="rr-hub-card__heading">
          <div className="rr-hub-card__title-row">
            <Title order={5} className="rr-hub-card__title">{title}</Title>
            {status}
          </div>
          <Text role="bodySmall" c="var(--rr-color-text-muted)">{description}</Text>
        </div>
      </div>
      <ul className="rr-hub-card__rows">
        {rows.map((row) => (
          <li key={row.to}>
            <Link to={row.to} className="rr-hub-card__row">
              <span className="rr-hub-card__label">{row.label}</span>
              {row.meta != null && <span className="rr-hub-card__meta">{row.meta}</span>}
              <span className="rr-hub-card__chevron">{Chevron}</span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
